<script setup lang="ts">
import type { PxlIcon } from "@/common/components/U/Icon";
import type { IAthleteHighlight, IBasicVideo, IClip, IEvent, IGameHighlight, IPlaylist } from "@/types";
import { EventModalShare } from "#components";
import { PLAYLIST_TYPE, VIDEO_TYPE } from "@/constants";

const { t } = useI18n();
const modal = useModal();

const props = defineProps<{
  video?: IEvent | IBasicVideo | null;
  playlist?: null | IPlaylist;
  label?: string;
  icon?: PxlIcon;
  getSharedTime?: () => number;
}>();

const shareTime = ref(0);

const isEvent = (vid?: IPlaylist | IEvent | IBasicVideo | null): vid is IEvent => vid?.type === VIDEO_TYPE.EVENT;
const isClip = (vid?: IPlaylist | IEvent | IBasicVideo | null): vid is IClip => vid?.type === VIDEO_TYPE.CLIP;
const isGameHighlight = (vid?: IPlaylist | IEvent | IBasicVideo | null): vid is IGameHighlight =>
  vid?.type === VIDEO_TYPE.GAME_HIGHLIGHT || vid?.type === "autohighlight";
const isAthleteHighlight = (vid?: IPlaylist | IEvent | IBasicVideo | null): vid is IAthleteHighlight => vid?.type === VIDEO_TYPE.ATHLETE_HIGHLIGHT;

const isAvailable = computed(() => {
  if (props.playlist || isClip(props.video) || isEvent(props.video)) {
    return true;
  }

  return isGameHighlight(props.video) || isAthleteHighlight(props.video);
});

const isDisabled = computed(() => {
  if (isClip(props.video)) {
    return props.video.isPending;
  } else if (props.playlist) {
    return props.playlist.type === PLAYLIST_TYPE.SYSTEM;
  }

  return false;
});

function onClick() {
  if (props.getSharedTime) {
    shareTime.value = props.getSharedTime();
  }

  modal.open(EventModalShare, { video: props.video, playlist: props.playlist, shareTime: shareTime.value });
}
</script>

<template>
  <UListItem
    v-if="isAvailable"
    :icon="icon || 'share'"
    :text="label || t('labels.share')"
    :disabled="isDisabled"
    :on-click="onClick"
  />
</template>
